import type {
  PlanGroupStatus,
  PlanProjectionGroup,
  PullRequestState,
  TaskStatus,
} from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type LegWait, legWire } from '../lib/plan-dag';
import { PlanLegDeployChips } from './PlanLegDeployChips';
import { StatusPill, type StatusTone } from './StatusPill';

// One slice (a goal's LEG, plan 101 slice 12) of an epic card: "Part n/m" + title +
// group-status pill in the header, then a fact line — the dispatched task's status, the PR
// (a link, its state, the merge commit), one deploy chip per environment, what the leg is
// WAITING on, and the upstreams it depends on. Presenter — <FleetEpicPanel/> resolves the
// DAG (`edges` / `mergeHeld` name sibling GROUP ids, not PR numbers) and hands each row its
// upstream lookups; a row never sees a raw group id.
//
// THE REPO IS PER SLICE (arc 101 slice 05). The panel groups rows under a heading per repo,
// so the row does not repeat it as a bare name; it rides the PR link instead, as
// `<repo>#<n>` — GitHub's own spelling — read off THIS group (`repoFullName`), never off
// the plan summary, whose `repoFullName` is the LEAD slice's repo and wrong for the rest.
//
// THE PR IS A LINK now (slice 12), to its own repo's pull request. It was plain text while
// the projection carried no repo; it has carried one since slice 05.
//
// EVERY EDGE INTO THE LEG SAYS ONE THING: an unreleased edge is a WAIT ("Waiting on X to
// deploy to production" at start, "Merge waits on X to merge" at merge), a released one is
// a plain "Depends on" — `lib/plan-dag.ts` `legWaits` / `releasedUpstreams` partition them.

/** An upstream sibling this slice waits on, resolved against the projection. */
export type SliceDependency = {
  readonly groupId: string;
  readonly title: string;
  /** The upstream group's PR number — null until its task opened one. */
  readonly pullRequestNumber: number | null;
};

export type PlanSliceRowProps = {
  readonly group: PlanProjectionGroup;
  /** Total slice count (the m in "Part n/m"); `group.position` is 0-based. */
  readonly total: number;
  readonly dependsOn: readonly SliceDependency[];
  /** What this leg is held on, at start or at merge — `legWaits`; none when omitted. */
  readonly waits?: readonly LegWait[];
};

/** The one sentence per wait; four literal keys so the coverage gate sees every one. */
function waitText(wait: LegWait): string {
  const vars = { leg: wait.upstreamTitle, environment: wait.environment ?? '' };
  if (wait.at === 'start') {
    return wait.release === 'merged'
      ? t('fleet.epic.wait.start_merged', vars)
      : t('fleet.epic.wait.start_deployed', vars);
  }
  return wait.release === 'merged'
    ? t('fleet.epic.wait.merge_merged', vars)
    : t('fleet.epic.wait.merge_deployed', vars);
}

/** A merge commit as the console shows one: seven characters, the whole sha on hover. */
const SHORT_SHA = 7;

const GROUP_STATUS_TONE: Record<PlanGroupStatus, StatusTone> = {
  pending: 'neutral',
  dispatched: 'live',
  merged: 'success',
  // A held group is awaiting approval (#2680). The pill is the only surface where
  // this state ever shows up — the projection's `landed`/`in_flight`/`stalled`
  // disposition is derived separately and reads `merged` as the lone release.
  // 'warning' is the closest tone: the work is paused, not failed.
  held: 'warning',
  // (#2696) A cancelled group — the operator pressed `plan cancel`. The
  // disposition carries `stalled(reason: 'cancelled')` for the dependent
  // sweep; the pill is the surface that says "this slice will never move".
  // 'danger' rather than 'neutral': a cancelled slice is a permanent state
  // change a maintainer must act on, not an idle one.
  cancelled: 'danger',
  // Plan 101 D7: a repair replaced the slice; the replacement carries the work, so the
  // replaced one is settled history, not a state to act on.
  superseded: 'neutral',
};

const GROUP_STATUS_LABEL: Record<PlanGroupStatus, string> = {
  pending: 'fleet.epic.status_pending',
  dispatched: 'fleet.epic.status_dispatched',
  merged: 'fleet.epic.status_merged',
  held: 'fleet.epic.status_held',
  // (#2696) A cancelled slice — the operator pressed `plan cancel` on a held
  // or partially-held plan, or the dispatched half of a plan whose task was
  // cancelled. Permanent: a later `plan approve` cannot resurrect it.
  cancelled: 'fleet.epic.status_cancelled',
  superseded: 'fleet.epic.status_superseded',
};

// Same fleet task lifecycle as the /fleet queue (TaskQueueItem), minus the
// outcome-kind nuance — the projection's task carries no `outcome_kind`.
const TASK_STATUS_TONE: Record<TaskStatus, StatusTone> = {
  pending: 'neutral',
  assigned: 'neutral',
  in_progress: 'live',
  completed: 'success',
  awaiting_ci: 'warning',
  awaiting_review: 'warning',
  mergeable: 'success',
  merged: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  // #6747: accent is for live work, and a paused task is not running.
  paused: 'neutral',
};

const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  pending: 'fleet.task_status_pending',
  assigned: 'fleet.task_status_assigned',
  in_progress: 'fleet.task_status_in_progress',
  completed: 'fleet.task_status_completed',
  awaiting_ci: 'fleet.task_status_awaiting_ci',
  awaiting_review: 'fleet.task_status_awaiting_review',
  mergeable: 'fleet.task_status_mergeable',
  merged: 'fleet.task_status_merged',
  failed: 'fleet.task_status_failed',
  cancelled: 'fleet.task_status_cancelled',
  paused: 'fleet.task_status_paused',
};

const PR_STATE_TONE: Record<PullRequestState, StatusTone> = {
  open: 'live',
  closed: 'neutral',
  merged: 'success',
};

const PR_STATE_LABEL: Record<PullRequestState, string> = {
  open: 'fleet.epic.pr_open',
  closed: 'fleet.epic.pr_closed',
  merged: 'fleet.epic.pr_merged',
};

export function PlanSliceRow(props: PlanSliceRowProps): JSX.Element {
  return (
    <li class="card plan-slice" data-status={props.group.status}>
      <div class="card__header">
        <div class="plan-slice__row">
          <h4 class="card__title plan-slice__name">{props.group.title}</h4>
          <StatusPill tone={GROUP_STATUS_TONE[props.group.status]}>
            {t(GROUP_STATUS_LABEL[props.group.status])}
          </StatusPill>
        </div>
        <p class="card__desc">
          {t('fleet.epic.part', { n: props.group.position + 1, m: props.total })}
        </p>
        <Show when={props.group.supersededBy}>
          {(group) => <p class="card__desc">{t('fleet.epic.replaced_by', { group: group() })}</p>}
        </Show>
      </div>
      <p class="card__content plan-slice__meta">
        <span class="plan-slice__task">
          <Show
            when={props.group.task}
            fallback={<span class="plan-slice__muted">{t('fleet.epic.no_task')}</span>}
          >
            {(task) => (
              <StatusPill tone={TASK_STATUS_TONE[task().status]}>
                {t(TASK_STATUS_LABEL[task().status])}
              </StatusPill>
            )}
          </Show>
        </span>
        <Show when={props.group.pullRequest}>
          {(pr) => (
            <span class="plan-slice__pr">
              <a
                class="plan-slice__pr-link"
                href={`https://github.com/${props.group.repoFullName}/pull/${pr().number}`}
                target="_blank"
                rel="noreferrer"
              >
                {t('fleet.epic.pr_ref', { repo: props.group.repoFullName, number: pr().number })}
              </a>
              <StatusPill tone={PR_STATE_TONE[pr().state]}>
                {t(PR_STATE_LABEL[pr().state])}
              </StatusPill>
              <Show when={pr().mergeCommitSha}>
                {(sha) => (
                  <code class="plan-slice__sha" title={sha()}>
                    {sha().slice(0, SHORT_SHA)}
                  </code>
                )}
              </Show>
            </span>
          )}
        </Show>
        <Show when={legWire(props.group).deploy.length > 0}>
          <PlanLegDeployChips deploy={legWire(props.group).deploy} />
        </Show>
        <For each={props.waits ?? []}>
          {(wait) => <span class="plan-slice__wait">{waitText(wait)}</span>}
        </For>
        <For each={props.dependsOn}>
          {(dep) => (
            <span class="plan-slice__dep">
              {dep.pullRequestNumber !== null
                ? t('fleet.epic.depends_on', { pr: dep.pullRequestNumber })
                : t('fleet.epic.depends_on_pending', { title: dep.title })}
            </span>
          )}
        </For>
      </p>
    </li>
  );
}
